import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MicButton from "@/components/MicButton";
import { Send, MessageCircle } from "lucide-react";

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) { return ""; }
}

// 1:1 message thread. participantId is null for a participant (their own thread),
// or the participant's id when used by a coach.
export default function ChatThread({ participantId = null, height = "h-[420px]" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = participantId ? { participant_id: participantId } : {};
      const { data } = await api.get("/messages", { params });
      setMessages(data || []);
    } catch (e) {
      console.error("Failed to load messages:", e);
    }
  }, [participantId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post("/messages", { text: t, participant_id: participantId || undefined });
      setText("");
      await load();
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="chat-thread" className="flex flex-col rounded-sm border border-stone-200 bg-white">
      <div ref={scrollRef} className={`${height} overflow-y-auto p-5 space-y-3`}>
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-stone-400">
            <MessageCircle size={26} strokeWidth={1.4} className="mb-2 text-stone-300" />
            <p className="text-sm">No messages yet. Say hello to start the conversation.</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-lg px-4 py-2.5 ${mine ? "bg-brand-primary text-white" : "bg-brand-parchment/60 text-stone-800"}`}>
                  {!mine && <div className="mb-0.5 text-[11px] uppercase tracking-wide opacity-70">{m.sender_role === "coach" ? "Coach" : m.sender_name || "Participant"}</div>}
                  <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.text}</p>
                  <div className={`mt-1 text-[11px] ${mine ? "text-white/70" : "text-stone-400"}`}>{fmtTime(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="border-t border-stone-100 p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            rows={1}
            placeholder="Write or speak a message…"
            className="flex-1 resize-none rounded-sm border border-stone-200 bg-transparent px-3 py-2 text-[15px] text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
          />
          <MicButton testid="chat-mic" getValue={() => text} onChange={(v) => setText(v)} />
          <button
            data-testid="chat-send"
            type="submit"
            disabled={sending || !text.trim()}
            className="inline-flex items-center gap-1.5 rounded-sm bg-brand-primary px-4 py-2 text-sm text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
          >
            <Send size={15} /> Send
          </button>
        </div>
      </form>
    </div>
  );
}
